import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { useAuth, canEdit } from "../../lib/auth";
import { toast } from "sonner";
import { ClipboardList, Plus, Trash2, Save, Printer, GripVertical, Calendar, Users, Truck } from "lucide-react";

const EMPTY_SLOT = { time: "", title: "", location: "", owner: "", notes: "" };
const newSlotUid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `slot_${Date.now()}_${Math.random().toString(16).slice(2, 8)}`;
const withUid = (s) => ({ _uid: newSlotUid(), ...s });

export default function RunOfShowPage() {
  const { user } = useAuth();
  const editable = canEdit(user);
  const [data, setData] = useState(null);
  const [eventName, setEventName] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [timeline, setTimeline] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/run-of-show", { withCredentials: true });
      setData(r.data);
      setEventName(r.data.event_name || "");
      setEventDate(r.data.event_date || "");
      setTimeline((r.data.timeline || []).map(withUid));
    } catch { toast.error("Failed to load Run of Show"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateSlot = (idx, patch) => setTimeline(timeline.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  const addSlot = () => setTimeline([...timeline, withUid(EMPTY_SLOT)]);
  const removeSlot = (idx) => setTimeline(timeline.filter((_, i) => i !== idx));
  const moveSlot = (idx, dir) => {
    const next = [...timeline];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setTimeline(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Strip client-only _uid before sending to backend
      const payload = timeline.map(({ _uid: _drop, ...rest }) => rest);
      await api.patch("/admin/run-of-show", {
        event_name: eventName, event_date: eventDate, timeline: payload,
      }, { withCredentials: true });
      toast.success("Run of Show saved.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!data) return <p className="text-white/60" data-testid="ros-loading">Loading…</p>;

  return (
    <div className="space-y-10" data-testid="ros-root">
      <div className="flex items-end justify-between flex-wrap gap-4 no-print">
        <div>
          <p className="text-overline text-aawta-pink mb-2 flex items-center gap-2"><ClipboardList size={14} /> RUN OF SHOW</p>
          <h1 className="font-display text-4xl text-white">Event-Day Command Sheet</h1>
          <p className="text-white/50 mt-2 text-sm">Editable timeline, auto-populated vendor load-in roster, and volunteer shift assignments. Print directly for production binders.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="ros-print-btn" onClick={() => window.print()} className="btn-ghost py-2.5 px-4">
            <Printer size={14} /> EXPORT PDF
          </button>
          {editable && (
            <button data-testid="ros-save-btn" onClick={save} disabled={saving} className="btn-pink disabled:opacity-50">
              <Save size={14} /> {saving ? "SAVING…" : "SAVE"}
            </button>
          )}
        </div>
      </div>

      {/* Print header — only visible in @media print */}
      <div className="hidden print:block mb-6">
        <p className="text-overline">AAWTA · {eventDate}</p>
        <h1 className="font-display text-3xl">{eventName} — Run of Show</h1>
      </div>

      {/* Event header */}
      <section className="border border-white/10 bg-aawta-charcoal/40 p-6 grid md:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-overline text-white/50 mb-1.5 block flex items-center gap-2"><Calendar size={12} /> Event Name</span>
          <input data-testid="ros-event-name" value={eventName} onChange={(e) => setEventName(e.target.value)} disabled={!editable}
                 className="w-full bg-aawta-jet/60 border border-white/10 focus:border-aawta-pink outline-none text-white px-3 py-2 text-sm disabled:opacity-70" />
        </label>
        <label className="block">
          <span className="text-overline text-white/50 mb-1.5 block">Event Date</span>
          <input data-testid="ros-event-date" type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} disabled={!editable}
                 className="w-full bg-aawta-jet/60 border border-white/10 focus:border-aawta-pink outline-none text-white px-3 py-2 text-sm disabled:opacity-70" />
        </label>
      </section>

      {/* Timeline */}
      <section>
        <div className="flex items-end justify-between mb-4 flex-wrap gap-2 no-print">
          <div>
            <h2 className="font-display text-2xl text-white">Day-of Timeline</h2>
            <p className="text-white/50 text-sm">Reorder and edit production blocks. Saves as a single source of truth for the team.</p>
          </div>
          {editable && (
            <button data-testid="ros-add-slot-btn" onClick={addSlot} className="btn-ghost py-2 px-3 text-xs">
              <Plus size={12} /> ADD BLOCK
            </button>
          )}
        </div>
        <div className="border border-white/10 overflow-hidden" data-testid="ros-timeline">
          <table className="w-full text-sm">
            <thead className="bg-aawta-charcoal/60 text-overline text-white/50 text-left">
              <tr>
                <th className="px-3 py-3 w-24">Time</th>
                <th className="px-3 py-3">Title</th>
                <th className="px-3 py-3">Location</th>
                <th className="px-3 py-3">Owner</th>
                <th className="px-3 py-3">Notes</th>
                <th className="px-3 py-3 w-20 no-print"></th>
              </tr>
            </thead>
            <tbody>
              {timeline.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-white/40">No blocks yet — click Add Block to start.</td></tr>
              )}
              {timeline.map((s, idx) => (
                <tr key={s._uid || idx} data-testid={`ros-row-${idx}`} className="border-t border-white/5">
                  <td className="px-3 py-2 align-top">
                    <input data-testid={`ros-time-${idx}`} value={s.time} onChange={(e) => updateSlot(idx, { time: e.target.value })} disabled={!editable}
                           placeholder="HH:MM" className="w-full bg-aawta-jet/60 border border-white/10 focus:border-aawta-pink outline-none text-white px-2 py-1.5 text-sm font-mono" />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <input data-testid={`ros-title-${idx}`} value={s.title} onChange={(e) => updateSlot(idx, { title: e.target.value })} disabled={!editable}
                           className="w-full bg-aawta-jet/60 border border-white/10 focus:border-aawta-pink outline-none text-white px-2 py-1.5 text-sm" />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <input data-testid={`ros-location-${idx}`} value={s.location || ""} onChange={(e) => updateSlot(idx, { location: e.target.value })} disabled={!editable}
                           className="w-full bg-aawta-jet/60 border border-white/10 focus:border-aawta-pink outline-none text-white px-2 py-1.5 text-sm" />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <input data-testid={`ros-owner-${idx}`} value={s.owner || ""} onChange={(e) => updateSlot(idx, { owner: e.target.value })} disabled={!editable}
                           className="w-full bg-aawta-jet/60 border border-white/10 focus:border-aawta-pink outline-none text-white px-2 py-1.5 text-sm" />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <input data-testid={`ros-notes-${idx}`} value={s.notes || ""} onChange={(e) => updateSlot(idx, { notes: e.target.value })} disabled={!editable}
                           className="w-full bg-aawta-jet/60 border border-white/10 focus:border-aawta-pink outline-none text-white px-2 py-1.5 text-sm" />
                  </td>
                  <td className="px-3 py-2 align-top no-print">
                    {editable && (
                      <div className="flex items-center gap-1">
                        <button data-testid={`ros-up-${idx}`} onClick={() => moveSlot(idx, -1)} title="Move up" className="text-white/40 hover:text-aawta-pink p-1"><GripVertical size={12} /></button>
                        <button data-testid={`ros-del-${idx}`} onClick={() => removeSlot(idx)} title="Remove" className="text-white/40 hover:text-red-400 p-1"><Trash2 size={12} /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Vendor Load-In Roster */}
      <section>
        <h2 className="font-display text-2xl text-white mb-2 flex items-center gap-2"><Truck size={18} className="text-aawta-pink" /> Vendor Load-In Roster</h2>
        <p className="text-white/50 text-sm mb-4">{data.vendors.length} approved vendor{data.vendors.length === 1 ? "" : "s"} (synced from approved Vendor submissions).</p>
        <div className="border border-white/10 overflow-x-auto" data-testid="ros-vendors">
          <table className="w-full text-sm">
            <thead className="bg-aawta-charcoal/60 text-overline text-white/50 text-left">
              <tr>
                <th className="px-3 py-3">Business</th>
                <th className="px-3 py-3">Contact</th>
                <th className="px-3 py-3">Category</th>
                <th className="px-3 py-3">Load-In</th>
                <th className="px-3 py-3">Booth</th>
                <th className="px-3 py-3">Phone</th>
                <th className="px-3 py-3">Email</th>
              </tr>
            </thead>
            <tbody>
              {data.vendors.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-white/40">No approved vendors yet. Approve vendor submissions in Inbox to populate this roster.</td></tr>
              )}
              {data.vendors.map((v) => (
                <tr key={v.id} data-testid={`ros-vendor-${v.id}`} className="border-t border-white/5">
                  <td className="px-3 py-2 text-white">{v.business_name || "—"}</td>
                  <td className="px-3 py-2 text-white/70">{v.contact_name || "—"}</td>
                  <td className="px-3 py-2 text-white/70 text-xs">{v.product_category || "—"}</td>
                  <td className="px-3 py-2 text-aawta-pink font-mono text-xs">{v.load_in_time || "—"}</td>
                  <td className="px-3 py-2 text-white/70 text-xs">{v.booth_size || "—"}</td>
                  <td className="px-3 py-2 text-white/70 text-xs">{v.phone || "—"}</td>
                  <td className="px-3 py-2 text-white/70 text-xs">{v.email || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Volunteer Shifts */}
      <section>
        <h2 className="font-display text-2xl text-white mb-2 flex items-center gap-2"><Users size={18} className="text-aawta-pink" /> Volunteer Shifts</h2>
        <p className="text-white/50 text-sm mb-4">{data.volunteers.length} approved volunteer{data.volunteers.length === 1 ? "" : "s"} (synced from approved Volunteer submissions).</p>
        <div className="border border-white/10 overflow-x-auto" data-testid="ros-volunteers">
          <table className="w-full text-sm">
            <thead className="bg-aawta-charcoal/60 text-overline text-white/50 text-left">
              <tr>
                <th className="px-3 py-3">Name</th>
                <th className="px-3 py-3">Availability</th>
                <th className="px-3 py-3">Role</th>
                <th className="px-3 py-3">Shirt</th>
                <th className="px-3 py-3">Phone</th>
                <th className="px-3 py-3">Email</th>
              </tr>
            </thead>
            <tbody>
              {data.volunteers.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-white/40">No approved volunteers yet. Approve volunteer submissions in Inbox to populate this roster.</td></tr>
              )}
              {data.volunteers.map((v) => (
                <tr key={v.id} data-testid={`ros-volunteer-${v.id}`} className="border-t border-white/5">
                  <td className="px-3 py-2 text-white">{v.name || "—"}</td>
                  <td className="px-3 py-2 text-aawta-pink font-mono text-xs">{Array.isArray(v.availability) ? v.availability.join(", ") : (v.availability || "—")}</td>
                  <td className="px-3 py-2 text-white/70 text-xs">{v.role_preference || "—"}</td>
                  <td className="px-3 py-2 text-white/70 text-xs">{v.tshirt_size || "—"}</td>
                  <td className="px-3 py-2 text-white/70 text-xs">{v.phone || "—"}</td>
                  <td className="px-3 py-2 text-white/70 text-xs">{v.email || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
